import * as formatting from './formatting';
import { fetchA } from '@bb/session-manager';

const isJsonResponse = (resp) => {
  return (resp.headers.get('content-type') || '')
    .toLowerCase()
    .includes('application/json');
};

const parseResponseBody = async (resp) => {
  const text = await resp.text();

  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (_error) {
    return text;
  }
};

const throwForBadResponse = async (resp) => {
  if (resp.ok) return;
  const body = await parseResponseBody(resp);
  const message =
    (typeof body === 'object' && body?.message) ||
    (typeof body === 'string' && body) ||
    resp.statusText ||
    'Request failed';
  const error = new Error(`Bad server response (${resp.status}): ${message}`);
  error.status = resp.status;
  error.body = body;
  throw error;
};

/**
 * This class is meant to be a helpful frontend interface for the Generic
 * Mongo Data Router found in v2 of the bbwebapi.
 */
export class MongoDataAPI {
  // Root url to append to
  baseUrl = new URL('/api/v2/mongo/', window.location.origin).toString();

  // Name from the mongoose schema within bbwebapi API
  mongoSchemaName = '';
  // A URL friendly version of the schema name
  urlId = '';

  /**
   *
   * @param {string} mongodbSchemaName Name of Schema from mongoose.
   */
  constructor(mongodbSchemaName) {
    this.mongoSchemaName = mongodbSchemaName;
    this.urlId = formatting.toUrlCase(mongodbSchemaName);
    this.baseUrl = new URL(this.urlId, this.baseUrl).toString();
  }

  /**
   * Data fetching from the V2 API within bbwebapi
   *
   * @param {object} params Various params
   * @param {boolean} params.count Whether or not to fetch total count.
   * @param {string[]} params.fields Fields to return.
   * @param {number} params.limit Number of items to return.
   * @param {number} params.page Page number for pagination.
   * @param {string[]} params.populate Replace _id with actual value from foreign collection.
   * @param {object} params.query Mongodb query.
   * @param {object} params.rename_fields Map that changes name of the fields in the response
   * @param {string} params.search Text to search collection for.
   * @param {number} params.skip Number of items to skip during fetch.
   * @param {string[]} params.sort list of fields to sort on, use minus sign
   *   i.e. "-field_name" to sort desc.
   * @param {boolean} params.stream  Response data is streamed in chunks, preferred for
   *   extremely large amounts of data. No metadata is returned.
   * @param {object} opts various options
   * @param {object} opts.headers extra headers to send
   * @returns object containing json response
   */
  async fetchData(params = {}, { headers = {} } = {}) {
    const qParams = formatting.serializeParams(params);
    const resp = await fetchA(`${this.baseUrl}?${qParams}`, {
      method: 'GET',
      headers,
    });
    if (headers.Accept?.includes('text/csv')) {
      const textResp = await resp.text();
      return textResp;
    } else if (
      headers.Accept?.includes(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    ) {
      const blobResp = await resp.blob();
      return blobResp;
    } else {
      const jsonResp = await parseResponseBody(resp);
      await throwForBadResponse(resp);
      return jsonResp;
    }
  }

  /**
   * Basic data creation from the V2 API within bbwebapi
   *
   * @param {object | object[]} docs the new documents to add
   * @returns object containing json response
   */
  async insertData(docs) {
    const resp = await fetchA(this.baseUrl, {
      method: 'POST',
      body: JSON.stringify({ insert: docs }),
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    return jsonResp;
  }

  /**
   * Basic data updating from the V2 API within bbwebapi
   *
   * @param {object} query Query representing which  documents to update
   * @param {object} update New values in the form {  key: value, ... }
   * @param {object} opts various options
   * @param {object} opts.queryParams extra query params to send
   * @returns object containing json response
   */
  async updateData(query, update, { queryParams = {} } = {}) {
    const qParams = formatting.serializeParams(queryParams);
    const resp = await fetchA(`${this.baseUrl}?${qParams}`, {
      method: 'PATCH',
      body: JSON.stringify({ query, update }),
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    return jsonResp;
  }

  /**
   * Data fetching by ID from the V2 API within bbwebapi
   *
   * @param {string} id Usually just the _id in mongo
   * @param {object} params Various params
   * @returns object containing json response
   */
  async fetchDataById(id, params = {}) {
    let url = `${this.baseUrl}/`;
    url = new URL(id, url).toString();
    const qParams = formatting.serializeParams(params);
    const resp = await fetchA(`${url}?${qParams}`);
    // Clean up for easy use
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    if (!jsonResp.docs?.length) {
      throw new Error(`No document found with _id ${id}`);
    }
    return jsonResp;
  }

  /**
   * Basic data patching by ID from the V2 API within bbwebapi
   *
   * @param {object} id Usually just _id
   * @param {object} update New values in the form {  key: value, ... }
   * @returns object containing json response
   */
  async updateDataById(id, update = {}) {
    let url = `${this.baseUrl}/`;
    url = new URL(id, url).toString();
    const resp = await fetchA(url, {
      method: 'PATCH',
      body: JSON.stringify({ update }),
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    return jsonResp;
  }

  /**
   * Basic data replace by ID from the V2 API within bbwebapi
   *
   * @param {object} id Usually just _id
   * @param {object} doc new doc to replace at id
   * @returns object containing json response
   */
  async replaceDataById(id, doc) {
    let url = `${this.baseUrl}/`;
    url = new URL(id, url).toString();
    const resp = await fetchA(url, {
      method: 'PATCH',
      body: JSON.stringify({ replace: doc }),
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    return jsonResp;
  }

  /**
   * Basic data removal by ID from the V2 API within bbwebapi
   *
   * @param {object} id Usually just _id
   * @returns object containing json response
   */
  async removeDataById(id) {
    let url = `${this.baseUrl}/`;
    url = new URL(id, url).toString();
    const resp = await fetchA(url, {
      method: 'DELETE',
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    return jsonResp;
  }

  /**
   * Basic data cloning by ID from the V2 API within bbwebapi
   *
   * @param {object} id Usually just _id
   * @param {object} opts various options
   * @param {object} opts.headers extra headers to send
   * @returns object containing json response
   */
  async cloneDataById(id) {
    let url = `${this.baseUrl}/`;
    url = new URL(`${id}/clone`, url).toString();
    const resp = await fetchA(url, {
      method: 'POST',
    });
    // Clean up for easy use
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    if (!jsonResp.docs?.length) {
      throw new Error(`No document returned for clone`);
    }
    return jsonResp?.docs[0];
  }

  /**
   * Upload row based data file to mongodb collection
   * @param {object} params various params
   * @param {object} params.files the files to upload
   * @param {object} params.docSet Set a static value on every row created
   * @param {object} params.async Use AsyncJob for uploads
   * @returns object containing json response
   */
  async uploadData(params) {
    const { files, docSet, ...restParams } = params;
    let url = `${this.baseUrl}/`;
    const qParams = formatting.serializeParams(restParams);
    url = new URL('upload', url).toString();
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f.file);
    }
    if (Object.keys(docSet || {}).length) {
      formData.append('docSet', docSet);
    }
    const resp = await fetchA(`${url}?${qParams}`, {
      method: 'POST',
      body: formData,
    });
    const jsonResp = await parseResponseBody(resp);
    await throwForBadResponse(resp);
    // Clean up for easy use
    return jsonResp;
  }
}
